import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db, firebaseConfig } from '../config/fbConfig';
import { collection, setDoc, doc, getDocs, updateDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import Modal from 'react-modal';
import Logo from '../images/school_logo.png';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import { createUserWithEmailAndPassword, getAuth } from "firebase/auth";
import { initializeApp, getApp, getApps } from 'firebase/app';

let secondaryApp;
let secondaryAuth;

if (!getApps().some(app => app.name === 'Secondary')) {
  secondaryApp = initializeApp(firebaseConfig, 'Secondary');
} else {
  secondaryApp = getApp('Secondary');
}

secondaryAuth = getAuth(secondaryApp);

Modal.setAppElement('#root');

const Parents = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [update, setUpdate] = useState(false);
  const [parents, setParents] = useState([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    address: '',
    phone1: '',
    phone2: '',
    email: '',
    isActive: true,
    status: 'Parent'
  });
  const [editingParent, setEditingParent] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAssignStudentsModalOpen, setIsAssignStudentsModalOpen] = useState(false);
  const [viewingParent, setViewingParent] = useState(null);
  const [deletingParent, setDeletingParent] = useState(null);
  const [assigningParent, setAssigningParent] = useState(null);
  const [students, setStudents] = useState([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [studentSearchQuery, setStudentSearchQuery] = useState("");
  const [isFetchingStaff, setIsFetchingStaff] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const usersCollectionRef = collection(db, "users");
  const studentsCollectionRef = collection(db, "students");

  useEffect(() => {
    fetchStaff();
    fetchStudents();
  }, []);

  const fetchStaff = async (searchTerm = "") => {
    setIsFetchingStaff(true);
    try {
      const normalizedSearchTerm = searchTerm.trim().toLowerCase();
      
      if (normalizedSearchTerm === "all") {
        // Fetch ALL parents
        const data = await getDocs(usersCollectionRef);
        const parentsOnly = data.docs.map(doc => ({ ...doc.data(), id: doc.id })).filter(t => t.status === 'Parent');
        setParents(parentsOnly);
      } 
      else if (searchTerm.trim()) {
        // Fetch all and filter locally for case-insensitive partial matching
        const data = await getDocs(usersCollectionRef);
        const allParents = data.docs.map(doc => ({ ...doc.data(), id: doc.id }));
        
        const filteredParents = allParents.filter(parent => {
          if (parent.status !== 'Parent') return false;
          
          const fullName = `${parent.firstName || ''} ${parent.lastName || ''}`.toLowerCase();
          const phone1 = (parent.phone1 || '').toLowerCase();
          const phone2 = (parent.phone2 || '').toLowerCase();
          const address = (parent.address || '').toLowerCase();
          
          return (
            fullName.includes(normalizedSearchTerm) ||
            phone1.includes(normalizedSearchTerm) ||
            phone2.includes(normalizedSearchTerm) ||
            address.includes(normalizedSearchTerm)
          );
        });
        
        setParents(filteredParents);
      } 
      else {
        // Default: show nothing when search is empty
        setParents([]);
      }
    } catch (error) {
      setError("Failed to fetch parents");
      setShowModal(true);
    } finally {
      setIsFetchingStaff(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const data = await getDocs(studentsCollectionRef);
      const studentsData = data.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      setStudents(studentsData);
    } catch (error) {
      setError("Failed to fetch students");
      setShowModal(true);
    }
  };

  const filteredStudents = students
  .filter((student) => {
    const fullName = `${student.firstName} ${student.lastName}`.toLowerCase();
    const query = studentSearchQuery.toLowerCase();
    return fullName.includes(query);
  })
  .sort((a, b) => {
    const aSelected = selectedStudentIds.includes(a.id);
    const bSelected = selectedStudentIds.includes(b.id);
    
    // If both are selected or both unselected, sort alphabetically
    if (aSelected === bSelected) {
      const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
      const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
      return nameA.localeCompare(nameB);
    }
    
    // Selected students come first
    return aSelected ? -1 : 1;
  });

  // Update the search input to trigger search on Enter or button click
  const handleSearch = (e) => {
    e.preventDefault();
    fetchStaff(searchQuery);
  };


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddParent = async () => {
    setError("");
    setSuccessMessage("");
    const { firstName, lastName, address, phone1, phone2, email } = formData;

    if (!firstName || !lastName || !address || !phone1 || !email) {
      setError("All fields except phone 2 are required");
      setShowModal(true);
      return;
    }

    setUpdate(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, email, '123456');
      const uid = userCredential.user.uid;

      const userDocRef = doc(db, "users", uid);
      await setDoc(userDocRef, {
        uid,
        firstName,
        lastName,
        address,
        phone1,
        phone2,
        email,
        status: "Parent",
        isActive: true,
        kids: [],
        createdAt: Timestamp.now(),
      });

      setSuccessMessage("Parent added successfully");
      fetchStaff();
      setFormData({
        firstName: '',
        lastName: '',
        address: '',
        phone1: '',
        phone2: '',
        email: '',
        isActive: true,
        status: 'Parent'
      });
      setIsAddModalOpen(false);
    } catch (error) {
      if (error.code === 'auth/email-already-in-use') {
        setError("Email already in use. Please use a different email.");
      } else {
        setError("Failed to add parent: " + error.message);
      }
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleUpdateParent = async () => {
    setError("");
    setSuccessMessage("");
    setUpdate(true);
    try {
      const parentDoc = doc(db, "users", editingParent.id);
      await updateDoc(parentDoc, {
        ...formData,
        isActive: formData.isActive !== undefined ? formData.isActive : true
      });
      setSuccessMessage("Parent updated successfully");
      fetchStaff();
      setEditingParent(null);
      setIsEditModalOpen(false);
    } catch (error) {
      setError("Failed to update parent: " + error.message);
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleDeleteParent = async () => {
    if (!deletingParent) return;
    setError("");
    setSuccessMessage("");
    setUpdate(true);
    try {
      const parentDoc = doc(db, "users", deletingParent.id);
      await deleteDoc(parentDoc);
      setSuccessMessage("Parent deleted successfully");
      fetchStaff();
      setIsDeleteModalOpen(false);
      setDeletingParent(null);
    } catch (error) {
      setError("Failed to delete parent: " + error.message);
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleAssignStudents = async () => {
    if (!assigningParent || selectedStudentIds.length === 0) {
      setError("Please select at least one student");
      setShowModal(true);
      return;
    }
    setError("");
    setSuccessMessage("");
    setUpdate(true);
    try {
      const parentDoc = doc(db, "users", assigningParent.id);
      await updateDoc(parentDoc, {
        kids: selectedStudentIds
      });
      setSuccessMessage("Students assigned successfully");
      fetchStaff();
      setIsAssignStudentsModalOpen(false);
      setAssigningParent(null);
      setSelectedStudentIds([]);
      setStudentSearchQuery("");
    } catch (error) {
      setError("Failed to assign students: " + error.message);
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleEditParent = (parent) => {
    setEditingParent(parent);
    setFormData({ ...parent });
    setIsEditModalOpen(true);
  };

  const handleDeleteClick = (parent) => {
    setDeletingParent(parent);
    setIsDeleteModalOpen(true);
  };

  const handleAssignStudentsClick = (parent) => {
    setAssigningParent(parent);
    setSelectedStudentIds(parent.kids || []);
    setStudentSearchQuery("");
    setIsAssignStudentsModalOpen(true);
  };

  const handleStudentSelection = (studentId) => {
    setSelectedStudentIds(prev =>
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  if (loading || !userData) return <Loader text={loading ? "LOADING" : "READING"} />;

  const allowedRoles = ['Admin', 'Cashier'];

  return (
    <>
      {update && <Loader text="PROCESSING" />}
      {allowedRoles.includes(userData.status) ? (
        <>
      <div className="top-elements row align-center space-between">
        <Link to="/home" className="back-link">
          <i className="fa-solid fa-arrow-left size3"></i>
        </Link>
        <div className="column align-center justify-center">
          <img src={Logo} className="middle-logo" alt="logo" />
        </div>
        <Link to="#" className="home-link" onClick={() => {fetchStaff()}}>
          <i className="fa-solid fa-rotate-right size4"></i>
        </Link>
      </div>
      <div className="login-page-items column align-center scroll-fixed">
        <h2 className="manage-page-heading caps size4">Manage Parents</h2>
        <button className="login-button login-button-d caps" onClick={() => setIsAddModalOpen(true)}>Add Parent</button>

        <div className="search-container">
          <form onSubmit={handleSearch} className="row space-between">
            <input
              type="text"
              placeholder="Search by name, phone or address ('All' for all parents)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch(e);
                }
              }}
            />
            <button type="submit" className="login-button login-button-search">
              <i className="fas fa-search"></i>
            </button>
          </form>
        </div>

        <div className="scroll-fixed scroll-fixed2">
          <div className="class-list class-list-d scrollable">
            {isFetchingStaff ? (
              <p className="male-female-stat">Loading parents...</p>
            ) : parents.length === 0 ? (
              <p className="male-female-stat">Search to view parents of enter 'All' to view all parents</p>
            ) : (
              parents.map((parent) => (
                <div key={parent.id} className="class-item">
                  <p>{parent.firstName} {parent.lastName} {!parent.isActive && <span className="size5 font-red">(Inactive)</span>} <br />({parent.phone1})<br /><span className="size5">{parent.address}</span></p>
                  <div className="column align-center justify-center gap-15">
                    <div className="row gap-15">
                        <button className="list-btn" onClick={() => { setViewingParent(parent); setIsViewModalOpen(true); }}>
                        <i className="fas fa-eye"></i>
                        </button>
                        <button className="list-btn" onClick={() => handleEditParent(parent)}>
                        <i className="fas fa-edit"></i>
                        </button>
                    </div>
                    <div className="row gap-15">
                        <button className="list-btn" onClick={() => handleDeleteClick(parent)}>
                        <i className="fas fa-trash"></i>
                        </button>
                        <button className="list-btn" onClick={() => handleAssignStudentsClick(parent)}>
                        <i className="fas fa-user-plus"></i>
                        </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}
      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}

      <Modal isOpen={isAddModalOpen} onRequestClose={() => setIsAddModalOpen(false)} contentLabel="Add Parent" className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Add Parent</h2>
        <form className="session-form">
          <input name="firstName" className="input-field" placeholder="First Name" value={formData.firstName} onChange={handleInputChange} />
          <input name="lastName" className="input-field" placeholder="Last Name" value={formData.lastName} onChange={handleInputChange} />
          <input name="address" className="input-field" placeholder="Address" value={formData.address} onChange={handleInputChange} />
          <input name="phone1" className="input-field" placeholder="Phone 1" value={formData.phone1} onChange={handleInputChange} />
          <input name="phone2" className="input-field" placeholder="Phone 2" value={formData.phone2} onChange={handleInputChange} />
          <input name="email" className="input-field" placeholder="Email" value={formData.email} onChange={handleInputChange} />
          <div className="row align-center gap-1">
            <button className="login-button" type="button" onClick={() => setIsAddModalOpen(false)}>Cancel</button>
            <button className="login-button" type="button" onClick={handleAddParent}>Add Parent</button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={isEditModalOpen} onRequestClose={() => {
          setIsEditModalOpen(false);
          setFormData({
            firstName: '',
            lastName: '',
            address: '',
            phone1: '',
            phone2: '',
            email: '',
            isActive: true,
            status: 'Parent'
          }); // Reset formData
          setEditingParent(null); // Reset editingTeacher
        }} contentLabel="Edit Parent" className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Edit Parent</h2>
        <form className="session-form">
          <input name="firstName" className="input-field" placeholder="First Name" value={formData.firstName} onChange={handleInputChange} />
          <input name="lastName" className="input-field" placeholder="Last Name" value={formData.lastName} onChange={handleInputChange} />
          <input name="address" className="input-field" placeholder="Address" value={formData.address} onChange={handleInputChange} />
          <input name="phone1" className="input-field" placeholder="Phone 1" value={formData.phone1} onChange={handleInputChange} />
          <input name="phone2" className="input-field" placeholder="Phone 2" value={formData.phone2} onChange={handleInputChange} />
          <select
            name="status"
            className="input-field"
            value={formData.status}
            onChange={handleInputChange}
          >
            <option value="">Select status</option>
            <option value="Parent">Parent</option>
            <option value="Cashier">Cashier</option>
            <option value="Teacher">Teacher</option>
            <option value="Admin">Admin</option>
          </select>
          <div className="row align-center gap-1">
            <label>
              <input
                type="checkbox"
                name="isActive"
                className="gold-tick male-female-stat"
                checked={formData.isActive}
                onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
              />
              Active
            </label>
          </div>
          <div className="row align-center gap-1">
            <button className="login-button" type="button" onClick={() => {
                setIsEditModalOpen(false);
                setFormData({
                  firstName: '',
                  lastName: '',
                  address: '',
                  phone1: '',
                  phone2: '',
                  email: '',
                  isActive: true,
                  status: 'Parent'
                }); // Reset formData
                setEditingParent(null); // Reset editingTeacher
              }}>Cancel</button>
            <button className="login-button" type="button" onClick={handleUpdateParent}>Update Parent</button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={isViewModalOpen} onRequestClose={() => setIsViewModalOpen(false)} contentLabel="View Parent Details" className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Parent Details</h2>
        {viewingParent ? (
          <div className="column gap-1" style={{ maxHeight: '500px', overflowY: 'auto' }}>
            <p className="male-female-stat"><strong>First Name:</strong> {viewingParent.firstName}</p>
            <p className="male-female-stat"><strong>Last Name:</strong> {viewingParent.lastName}</p>
            <p className="male-female-stat"><strong>Address:</strong> {viewingParent.address}</p>
            <p className="male-female-stat"><strong>Phone 1:</strong> {viewingParent.phone1}</p>
            <p className="male-female-stat"><strong>Phone 2:</strong> {viewingParent.phone2}</p>
            <p className="male-female-stat"><strong>Email:</strong> {viewingParent.email}</p>
            <p className="male-female-stat"><strong>Status:</strong> {viewingParent.status}</p>
            <p className="font-red male-female-stat"><strong>Active:</strong> {viewingParent.isActive ? 'Yes' : 'No'}</p>
            <p className="male-female-stat"><strong>Kids:</strong> {viewingParent.kids && viewingParent.kids.length > 0 
              ? viewingParent.kids
                  .map(id => {
                    const student = students.find(s => s.id === id);
                    return student ? `${student.firstName} ${student.lastName} (${student.admissionNumber})` : 'Unknown';
                  })
                  .join(', ')
              : 'None'}</p>
            <button type="button" className="login-button caps" onClick={() => setIsViewModalOpen(false)}>
              Close
            </button>
          </div>
        ) : (
          <p>No parent selected</p>
        )}
      </Modal>

      <Modal isOpen={isDeleteModalOpen} onRequestClose={() => setIsDeleteModalOpen(false)} contentLabel="Delete Parent Confirmation" className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Confirm Deletion</h2>
        {deletingParent ? (
          <div className="column gap-1">
            <p className="male-female-stat">Are you sure you want to delete <strong>{deletingParent.firstName} {deletingParent.lastName}</strong>?</p>
            <p className="male-female-stat">This action cannot be undone.</p>
            <div className="row align-center gap-1">
              <button className="login-button" type="button" onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
              <button className="login-button font-red" type="button" onClick={handleDeleteParent}>Delete</button>
            </div>
          </div>
        ) : (
          <p className="male-female-stat">No parent selected</p>
        )}
      </Modal>

      <Modal isOpen={isAssignStudentsModalOpen} onRequestClose={() => setIsAssignStudentsModalOpen(false)} contentLabel="Assign Students" className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Assign Students to {assigningParent ? `${assigningParent.firstName} ${assigningParent.lastName}` : 'Parent'}</h2>
        <div className="column gap-1">
          <input
            type="text"
            placeholder="Search students by name"
            className="input-field"
            value={studentSearchQuery}
            onChange={(e) => setStudentSearchQuery(e.target.value)}
          />
          {filteredStudents.length === 0 ? (
            <p>No students match your search</p>
          ) : (
            <div className="scrollable" style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {filteredStudents.map((student) => (
                <div key={student.id} className="row align-center gap-1">
                  <input
                    type="checkbox"
                    className="gold-tick"
                    checked={selectedStudentIds.includes(student.id)}
                    onChange={() => handleStudentSelection(student.id)}
                  />
                  <label className="male-female-stat">{student.firstName} {student.lastName} {student.middleName} ({student.admissionNumber})</label>
                </div>
              ))}
            </div>
          )}
          <div className="row align-center gap-1">
            <button className="login-button" type="button" onClick={() => setIsAssignStudentsModalOpen(false)}>Cancel</button>
            <button className="login-button" type="button" onClick={handleAssignStudents}>Assign Students</button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Parents;